import React, { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import supabase from '../supabase/supabase';

function NoticeDisplayContent() {
  const [searchParams] = useSearchParams();
  const studioId = searchParams.get('s');

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeString, setTimeString] = useState('');

  useEffect(() => {
    document.title = "StudioSign | Active Notice";
  }, []);

  useEffect(() => {
    if (!studioId) { setLoading(false); return; }

    const fetchSettings = async () => {
      const { data, error } = await supabase
        .from('studio_settings')
        .select('id, studio_name, show_name, show_clock, timezone, notice_header, notice_subtext')
        .eq('id', studioId)
        .single();

      if (data && !error) setSettings(data);
      setLoading(false);
    };

    fetchSettings();

    const channel = supabase
      .channel(`studio_settings_${studioId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'studio_settings', filter: `id=eq.${studioId}` },
        (payload) => { setSettings((prev) => ({ ...prev, ...payload.new })); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [studioId]);

  useEffect(() => {
    let wakeLock = null;
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) wakeLock = await navigator.wakeLock.request('screen');
      } catch (err) { console.error(`${err.name}, ${err.message}`); }
    };
    requestWakeLock();
    const handleVisibilityChange = () => {
      if (wakeLock !== null && document.visibilityState === 'visible') requestWakeLock();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      if (wakeLock) wakeLock.release().catch(console.error);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const updateTime = () => {
      const tz = settings?.timezone;
      const now = new Date();
      try {
        const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...(tz ? { timeZone: tz } : {}) });
        const dateStr = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...(tz ? { timeZone: tz } : {}) }).toUpperCase();
        setTimeString(`${dateStr} • ${timeStr}`);
      } catch (e) {
        const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        const dateStr = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
        setTimeString(`${dateStr} • ${timeStr}`);
      }
    };
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, [settings?.timezone]);

  const handleDoubleTap = async () => {
    if (!document.fullscreenElement) {
      try { await document.documentElement.requestFullscreen(); }
      catch (err) { console.error('Fullscreen request failed:', err); }
    }
  };

  if (!studioId || loading || !settings) {
    return (
      <div className="w-screen h-screen bg-zinc-950 flex items-center justify-center transition-colors duration-1000 ease-in-out">
        <span className="text-zinc-800 text-sm font-mono">Awaiting Studio ID...</span>
      </div>
    );
  }

  return (
    // ── FIX: removed font-['Inter'] — now inherits global font from index.css
    // exactly like MainDisplay and LobbyDisplay do
    <div
      className="w-screen h-screen overflow-hidden flex flex-col relative bg-zinc-950 text-white cursor-default transition-colors duration-1000 ease-in-out"
      onDoubleClick={handleDoubleTap}
    >
      {/* ── Main content ── */}
      <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-12 transition-all duration-1000 ease-in-out">
        {settings.notice_header ? (
          <h1 className="font-extrabold tracking-tighter text-white text-[10vmax] leading-none max-w-[90vw] break-words transition-opacity duration-700 ease-in-out">
            {settings.notice_header}
          </h1>
        ) : null}
        {settings.notice_subtext ? (
          // ── FIX: font-semibold text-white → font-medium text-zinc-200
          // matches LobbyDisplay lobby_subtext exactly
          <p className={`font-medium text-zinc-200 text-[2.5vmax] leading-tight max-w-[80vw] break-words transition-opacity duration-700 ease-in-out ${settings.notice_header ? 'mt-6' : 'mt-0'}`}>
            {settings.notice_subtext}
          </p>
        ) : null}
      </div>

      {/* ── Bottom bar — studio name + clock pills ── */}
      {/* ── FIX: pill style aligned to LobbyDisplay top bar: bg-white/5, text-white/60 ── */}
      <div className="absolute bottom-8 w-full px-12 flex justify-between items-end gap-6 transition-opacity duration-700 ease-in-out">
        <div className="flex-1 flex justify-start">
          {settings.show_name && settings.studio_name?.trim() && (
            <div className="px-6 py-3 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 flex items-center">
              <div className="text-white/60 font-semibold tracking-widest uppercase text-[1.2vmax] max-w-[40vw] text-left leading-snug break-words line-clamp-2 transition-opacity duration-700 ease-in-out">
                {settings.studio_name}
              </div>
            </div>
          )}
        </div>
        <div className="flex-1 flex justify-end">
          {settings.show_clock && (
            <div className="px-6 py-3 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 flex items-center">
              <div className="text-white/60 font-semibold tracking-widest uppercase text-[1.2vmax] whitespace-nowrap transition-opacity duration-700 ease-in-out">
                {timeString}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function NoticeDisplay() {
  return (
    <Suspense fallback={
      <div className="w-screen h-screen bg-zinc-950 flex items-center justify-center">
        <span className="text-zinc-800 text-sm font-mono">Awaiting Studio ID...</span>
      </div>
    }>
      <NoticeDisplayContent />
    </Suspense>
  );
}